<template>
    <div class="popup-backdrop">
        <div class="popup-modal">
            <div class="popup-header">
                <span class="popup-title">환자 불러오기</span>
            </div>
            <template v-if="selectedData == 'api'">
                <div></div>
            </template>
            <template v-if="selectedData == 'excel'">
                <div class="excel-section" v-if="step == 1">
                    <!-- 안내 문구 -->
                    <div class="excel-info-box">
                        <div class="excel-info-header">
                            환자 정보는 지정된 엑셀 양식 파일로만 업로드할 수 있습니다.
                        </div>
                        <div class="excel-info-content">
                            <span class="highlight-text">(형식이 다를 경우 업로드가 제한됩니다.) → 엑셀 양식 다운로드
                                (<a href="javascript:;" @click="downSampleExcel">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M12 2C12.5523 2 13 2.44772 13 3V13.5858L15.2929 11.2929C15.6834 10.9024 16.3166 10.9024 16.7071 11.2929C17.0976 11.6834 17.0976 12.3166 16.7071 12.7071L12.7071 16.7071C12.3166 17.0976 11.6834 17.0976 11.2929 16.7071L7.29289 12.7071C6.90237 12.3166 6.90237 11.6834 7.29289 11.2929C7.68342 10.9024 8.31658 10.9024 8.70711 11.2929L11 13.5858V3C11 2.44772 11.4477 2 12 2Z"/>
                                        <path d="M6 18C6 17.4477 5.55228 17 5 17C4.44772 17 4 17.4477 4 18V19C4 20.6569 5.34315 22 7 22H17C18.6569 22 20 20.6569 20 19V18C20 17.4477 19.5523 17 19 17C18.4477 17 18 17.4477 18 18V19C18 19.5523 17.5523 20 17 20H7C6.44772 20 6 19.5523 6 19V18Z"/>
                                    </svg>
                                </a>)
                            </span>
                        </div>
                    </div>
                    <!-- 파일 업로드 영역 -->
                    <div class="file-upload-container">
                        <div class="file-upload-box" 
                             :class="{ 'file-selected': selectedFile }"
                             @click="triggerFileInput"
                             @dragover.prevent
                             @drop.prevent="handleFileDrop">
                            <div class="file-upload-content">
                                <div v-if="!selectedFile" class="file-upload-placeholder">
                                    <div class="upload-text">파일을 올리시려면 클릭해주시길 바랍니다.</div>
                                </div>
                                <div v-else class="file-selected-info">
                                    <div class="file-name">{{ selectedFile.name }}</div>
                                    <div class="file-size">{{ formatFileSize(selectedFile.size) }}</div>
                                </div>
                            </div>
                            <input 
                                type="file" 
                                ref="fileInput"
                                @change="handleFileSelect"
                                accept=".xlsx,.xls"
                                style="display: none"
                            >
                        </div>
                    </div>
                    <div class="popup-btn-group mt20">
                        <button class="popup-cancel-btn" @click="closePopup">취소</button>
                        <button class="popup-save-btn" v-if="selectedFile" @click="uploadFile">불러오기</button>
                    </div>
                </div>
                <div class="excel-section" v-if="step == 2">
                    <span :class="{'success-msg': status == 'success', 'error-msg': status != 'success'}">{{ msg }}</span>
                    <div class="popup-btn-group mt20">
                        <button class="popup-cancel-btn" @click="initUpload" v-if="status == 'success'">취소</button>
                        <button class="popup-save-btn" @click="saveFile">확인</button>
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>
<script setup>
    import { onMounted, ref }   from 'vue'
    import api, {urls}          from "@/api";
    import { useModal }         from '@/composables/useModal'
    import { useAuthStore }     from '@/stores/auth';
    import { useAppAlert }      from "@/composables/useAppAlert";
    
    const { showAlert, confirm } = useAppAlert()
    const {saveAndClose} = useModal()
    const authStore      = useAuthStore();
    const inst_cd        = authStore.getUser?.inst_cd;

    const emit = defineEmits(['close'])

    const selectedData  = ref('excel') // api 기능 구현 시 빈 값으로 전환
    const selectedFile  = ref(null)
    const fileInput     = ref(null)
    const step          = ref(1)
    const msg           = ref('')
    const status        = ref('')

    const closePopup = () => {
        emit('close')
    }

    const downSampleExcel = async() => {
        try{
            const templateUrl = `${location.origin}/resource/file/환자정보_업로드_템플릿.xlsx`
            
            const link      = document.createElement('a')
            link.href       = templateUrl
            link.setAttribute("download", "환자정보_업로드_템플릿.xlsx")

            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        }catch(err){
            showAlert('템플릿 파일을 찾을 수 없습니다.')
        }
    }
    
    const triggerFileInput = () => {
        fileInput.value?.click()
    }

    const handleFileSelect = (event) => {
        const file = event.target.files[0]
        if (file) {
            selectedFile.value = file
        }
    }

    const handleFileDrop = (event) => {
        const files = event.dataTransfer.files
        if (files.length > 0) {
            selectedFile.value = files[0]
        }
    }

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes'
        const k = 1024
        const sizes = ['Bytes', 'KB', 'MB', 'GB']
        const i = Math.floor(Math.log(bytes) / Math.log(k))
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    }

    const uploadFile = () => {
        if (selectedFile.value) {
            const validFileTypes = [
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
                'application/vnd.ms-excel' // .xls
            ];

            // 확장자
            const fileName = selectedFile.value.name.toLowerCase()
            const isExcelFile = fileName.endsWith('.xlsx') || fileName.endsWith('.xls')
            // 파일 타입 검사
            if (!validFileTypes.includes(selectedFile.value.type) && !isExcelFile) {
                showAlert("지원하지 않는 파일 형식입니다.")
                return
            }
            // 파일 사이즈 검사(10MB 제한)
            if ((selectedFile.value.size / (1024 * 1024)) > 10) {
                showAlert("파일 크기는 10MB를 넘을 수 없습니다.")
                return
            }
            const params = new FormData()
            params.append('file', selectedFile.value)
            params.append('instCd', inst_cd)

            api.post(urls.patientAdd.uploadExcel, params, {
                headers: {'Content-Type': 'multipart/form-data'}
            }).then(res => {
                const data = res.data

                if(data.count == 0){
                    showAlert('불러올 환자가 없습니다. 다른 파일을 선택해주세요.')
                    initUpload()
                    return
                }else {
                    step.value = 2
                    msg.value = data.msg
                    status.value = data.status

                }
            }).catch(err =>{
                showAlert('환자 목록을 불러오는데 실패했습니다.')
            })
        } else {
            showAlert('업로드할 파일을 선택해주세요.')
        }
    }

    const saveFile = () => {
        if(status.value != 'success'){
            showAlert('불러올 환자가 없습니다. 다른 파일을 선택해주세요.')
            initUpload()
        }else{
            saveAndClose('uploadPatientModal', true)
        }
    }

    const initUpload = () => {
        selectedFile.value = null
        step.value = 1
        msg.value = ''
        status.value = ''
        if (fileInput.value) {
            fileInput.value.value = ''
        }
    }
</script>